"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  TreeDiagram,
  type TreeOptions,
} from "@/components/tools/ecology/tree-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { colorIndex } from "@/lib/tool-kit/charts/color";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type NewickError,
  parseNewick,
  toNewick,
} from "@/lib/tool-kit/ecology/newick";
import {
  type CladeOrder,
  type LaidOutNode,
  layoutTree,
  orderedTree,
  type TreeStyle,
  treeStats,
} from "@/lib/tool-kit/ecology/phylo-layout";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const PHYLOGENETIC_TREE_MAKER_STRINGS = {
  treeLabel: "Your tree in Newick format",
  treeHint:
    "Nested parentheses with optional names, branch lengths after colons and support values on internal nodes, ending in a semicolon. Quote names that contain spaces or punctuation; [comments] are ignored.",
  exampleTree:
    "((((Human:0.0065,Chimpanzee:0.0065)95:0.0037,Gorilla:0.0102)88:0.0066,Orangutan:0.0168)100:0.0111,Gibbon:0.0279);",
  exampleTitle: "Illustrative ape tree",
  styleLabel: "Tree style",
  stylePhylogram: "Phylogram: branch lengths drawn to scale",
  styleCladogram: "Cladogram: branching order only",
  orderLabel: "Order of branches",
  orderGiven: "As written",
  orderAscending: "Smallest clades first",
  orderDescending: "Largest clades first",
  showLengths: "Write branch lengths on the branches",
  showLabels: "Show support values and clade names",
  alignTips: "Align tip labels in a column (dotted guides)",
  italic: "Italic tip labels, for species names",
  tips: "Tips",
  internal: "Internal nodes",
  totalLength: "Total branch length",
  height: "Longest root-to-tip distance",
  ultrametricNote:
    "Every tip is the same distance from the root, as in a tree built with a molecular clock.",
  rootNote:
    "The root has {count} branches. Unrooted trees are written this way; this picture puts the root at the left, but no ancestor is implied.",
  noLengthsNote:
    "This tree has no branch lengths, so it is drawn as a cladogram.",
  missingNote:
    "one: {count} branch has no length and is drawn with length 0.|other: {count} branches have no length and are drawn with length 0.",
  extraNote:
    "one: {count} more tree was found after the first; only the first is drawn.|other: {count} more trees were found after the first; only the first is drawn.",
  tooMany: "This tree has {count} tips; the drawing is limited to {limit}.",
  errEmpty: "Paste a tree in Newick format, for example (A:0.1,B:0.2);",
  errUnclosed: "The parenthesis at character {pos} is never closed.",
  errUnexpected: "Unexpected “{char}” at character {pos}.",
  errUnexpectedEnd: "The tree ends unexpectedly at character {pos}.",
  errBadLength: "“{text}” after a colon at character {pos} is not a number.",
  errQuote: "The quoted name starting at character {pos} is never closed.",
  errComment: "The [comment] starting at character {pos} is never closed.",
  errTooLarge:
    "This tree is too large to draw: the limit is 5,000 nodes and 1,000 levels of nesting.",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  colorLabel: "Branch color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy Newick",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the tree as a table",
  columnNode: "Node",
  columnDepth: "Depth",
  columnLength: "Branch length",
  columnSupport: "Support",
  columnTips: "Tips below",
  internalNode: "(internal node)",
  unnamedTip: "(unnamed tip)",
  chartLabel: "Phylogenetic tree",
  chartDescription: "{style} with {tips}. Tips from top to bottom: {list}.",
  descPhylogram: "Phylogram",
  descCladogram: "Cladogram",
  tipsCount: "one:{count} tip|other:{count} tips",
} as const;

type Strings = typeof PHYLOGENETIC_TREE_MAKER_STRINGS;

const ID = "ptm";
const MAX_TIPS = 250;

function errorText(error: NewickError, strings: Strings): string {
  const pos = String(error.position + 1);
  switch (error.code) {
    case "empty":
      return strings.errEmpty;
    case "unclosed":
      return t(strings.errUnclosed, { pos });
    case "unexpected":
      return error.detail
        ? t(strings.errUnexpected, { char: error.detail, pos })
        : t(strings.errUnexpectedEnd, { pos });
    case "badLength":
      return t(strings.errBadLength, { text: error.detail, pos });
    case "unclosedQuote":
      return t(strings.errQuote, { pos });
    case "unclosedComment":
      return t(strings.errComment, { pos });
    default:
      return strings.errTooLarge;
  }
}

function tableRows(
  nodes: readonly LaidOutNode[],
  strings: Strings,
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string
): string[][] {
  return nodes.map((node) => [
    node.name ?? (node.isLeaf ? strings.unnamedTip : strings.internalNode),
    fmt(node.depth),
    node.length === null
      ? ""
      : fmt(node.length, { maximumSignificantDigits: 6 }),
    node.support ?? "",
    fmt(node.leafCount),
  ]);
}

export default function PhylogeneticTreeMakerWidget() {
  const strings = useToolUi(PHYLOGENETIC_TREE_MAKER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [raw, setRaw] = useState<string>(strings.exampleTree);
  const [treeStyle, setTreeStyle] = useState<TreeStyle>("phylogram");
  const [order, setOrder] = useState<CladeOrder>("given");
  const [showLengths, setShowLengths] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [alignTips, setAlignTips] = useState(true);
  const [italic, setItalic] = useState(false);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "black",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parsed = parseNewick(raw);
  const root = parsed.root ? orderedTree(parsed.root, order) : null;
  const stats = root ? treeStats(root) : null;
  const drawable = root !== null && stats !== null && stats.tips <= MAX_TIPS;
  const layout = drawable && root ? layoutTree(root, treeStyle, "given") : null;
  const options: TreeOptions = {
    showLengths,
    showLabels,
    alignTips,
    italic,
    color: CHART_COLORS[colorIndex(style.color)],
  };

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.treeHint}
        id={`${ID}-tree`}
        label={strings.treeLabel}
        onChange={setRaw}
        rows={5}
        value={raw}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.styleLabel}
          name={`${ID}-style`}
          onChange={setTreeStyle}
          options={[
            { value: "phylogram", label: strings.stylePhylogram },
            { value: "cladogram", label: strings.styleCladogram },
          ]}
          value={treeStyle}
        />
        <SelectField
          id={`${ID}-order`}
          label={strings.orderLabel}
          onChange={setOrder}
          options={[
            { value: "given", label: strings.orderGiven },
            { value: "ascending", label: strings.orderAscending },
            { value: "descending", label: strings.orderDescending },
          ]}
          value={order}
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Checkbox
          checked={showLengths}
          label={strings.showLengths}
          onChange={setShowLengths}
        />
        <Checkbox
          checked={showLabels}
          label={strings.showLabels}
          onChange={setShowLabels}
        />
        <Checkbox
          checked={alignTips}
          label={strings.alignTips}
          onChange={setAlignTips}
        />
        <Checkbox
          checked={italic}
          label={strings.italic}
          onChange={setItalic}
        />
      </div>
      <div aria-live="polite" className="space-y-2">
        {parsed.error ? (
          <ErrorNotice>{errorText(parsed.error, strings)}</ErrorNotice>
        ) : null}
        {stats && !drawable ? (
          <Notice>
            {t(strings.tooMany, {
              count: fmt(stats.tips),
              limit: fmt(MAX_TIPS),
            })}
          </Notice>
        ) : null}
        {stats && layout ? (
          <Facts
            extra={parsed.extraTrees}
            fmt={fmt}
            layoutStyle={layout.style}
            locale={locale}
            missing={layout.missingLengths}
            requested={treeStyle}
            stats={stats}
            strings={strings}
          />
        ) : null}
      </div>
      {root && layout && stats ? (
        <ChartFigure
          apa={style.apa}
          data={toNewick(root)}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "phylogenetic-tree")}
          labels={strings}
          note={style.note}
          table={{
            head: [
              strings.columnNode,
              strings.columnDepth,
              strings.columnLength,
              strings.columnSupport,
              strings.columnTips,
            ],
            rows: tableRows(layout.nodes, strings, fmt),
          }}
          title={style.title}
        >
          <TreeDiagram
            description={t(strings.chartDescription, {
              style:
                layout.style === "phylogram"
                  ? strings.descPhylogram
                  : strings.descCladogram,
              tips: tPlural(strings.tipsCount, locale, stats.tips, {
                count: fmt(stats.tips),
              }),
              list: layout.tips
                .map((tip) => layout.nodes[tip].name ?? "")
                .join(", "),
            })}
            fmt={fmt}
            id={`${ID}-tree-svg`}
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
            options={options}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        colorLabels={strings}
        idPrefix={ID}
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}

function Facts({
  stats,
  layoutStyle,
  requested,
  missing,
  extra,
  strings,
  fmt,
  locale,
}: {
  stats: ReturnType<typeof treeStats>;
  layoutStyle: TreeStyle;
  requested: TreeStyle;
  missing: number;
  extra: number;
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  locale: string;
}) {
  const digits = { maximumSignificantDigits: 6 } as const;
  return (
    <>
      <StatGrid>
        <StatCard label={strings.tips} value={fmt(stats.tips)} />
        <StatCard label={strings.internal} value={fmt(stats.internal)} />
        {stats.hasLengths ? (
          <StatCard
            label={strings.totalLength}
            value={fmt(stats.totalLength, digits)}
          />
        ) : null}
        {stats.hasLengths ? (
          <StatCard label={strings.height} value={fmt(stats.height, digits)} />
        ) : null}
      </StatGrid>
      {requested === "phylogram" && layoutStyle === "cladogram" ? (
        <Notice>{strings.noLengthsNote}</Notice>
      ) : null}
      {stats.ultrametric && layoutStyle === "phylogram" ? (
        <p className={HINT}>{strings.ultrametricNote}</p>
      ) : null}
      {stats.rootDegree >= 3 ? (
        <p className={HINT}>
          {t(strings.rootNote, { count: fmt(stats.rootDegree) })}
        </p>
      ) : null}
      {missing > 0 ? (
        <Notice>
          {tPlural(strings.missingNote, locale, missing, {
            count: fmt(missing),
          })}
        </Notice>
      ) : null}
      {extra > 0 ? (
        <Notice>
          {tPlural(strings.extraNote, locale, extra, { count: fmt(extra) })}
        </Notice>
      ) : null}
    </>
  );
}
